import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Lightbulb, Mail, Send } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { createContact } from "@/lib/api";

const inputCls =
  "w-full rounded-xl border border-white/15 bg-black/50 px-4 py-3 text-sm text-white placeholder:text-slate-600 focus:border-blaze focus:outline-none";

export default function Contact() {
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    document.title = "Contact & event-tips | WanneerKaartverkoop.nl";
    window.scrollTo(0, 0);
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await createContact(form);
      toast.success("Bericht verzonden!", {
        description: "Bedankt voor je tip — we nemen hem zo snel mogelijk in behandeling.",
      });
      setForm({ name: "", email: "", message: "" });
    } catch {
      toast.error("Versturen mislukt. Probeer het opnieuw of mail ons direct.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-obsidian text-white" data-testid="contact-page">
      <Header onOrganizer={() => nav("/contact")} />
      <main className="pt-16">
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          >
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-blaze">Contact</p>
            <h1 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
              Event-tip of correctie? <span className="text-blaze">Laat het ons weten.</span>
            </h1>
            <p className="mt-4 max-w-2xl text-slate-400">
              Mis je een kaartverkoop, klopt een datum niet, of wil je als organisator je event
              aanmelden? Stuur ons een bericht — we lezen alles.
            </p>
          </motion.div>

          <div className="mt-12 grid gap-8 lg:grid-cols-[1fr_1.2fr]">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="space-y-4"
            >
              <div className="rounded-2xl border border-white/10 bg-panel p-6">
                <div className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-blaze/15">
                    <Mail className="h-5 w-5 text-blaze" />
                  </span>
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">
                    Mail ons direct
                  </p>
                </div>
                <a
                  href="mailto:info@wanneerkaartverkoop.nl"
                  data-testid="contact-email-link"
                  className="mt-4 block font-display text-xl font-bold text-blaze transition-colors hover:text-blaze-hover"
                >
                  info@wanneerkaartverkoop.nl
                </a>
                <p className="mt-2 text-xs text-slate-500">
                  We reageren doorgaans binnen twee werkdagen.
                </p>
              </div>
              <div className="rounded-2xl border border-white/10 bg-panel p-6">
                <div className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-volt/10">
                    <Lightbulb className="h-5 w-5 text-volt" />
                  </span>
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">
                    Waar je ons voor kunt tippen
                  </p>
                </div>
                <ul className="mt-4 space-y-2.5 text-sm text-slate-400">
                  <li className="flex gap-2.5"><span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-volt" />Nieuwe kaartverkoop of pre-sale die wij missen</li>
                  <li className="flex gap-2.5"><span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-volt" />Correcties op data, tijden of locaties</li>
                  <li className="flex gap-2.5"><span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-volt" />Aanmelding van je eigen event als organisator</li>
                  <li className="flex gap-2.5"><span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-volt" />Ideeën om de site beter te maken</li>
                </ul>
              </div>
            </motion.div>

            <motion.form
              onSubmit={submit}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.25 }}
              className="h-fit rounded-2xl border border-white/10 bg-panel p-6 sm:p-8"
              data-testid="contact-form"
            >
              <h2 className="font-display text-lg font-bold">Stuur een bericht</h2>
              <div className="mt-5 grid gap-3 sm:grid-cols-2">
                <input
                  required
                  value={form.name}
                  onChange={set("name")}
                  placeholder="Je naam *"
                  data-testid="contact-name-input"
                  className={inputCls}
                />
                <input
                  required
                  type="email"
                  value={form.email}
                  onChange={set("email")}
                  placeholder="Je e-mailadres *"
                  data-testid="contact-email-input"
                  className={inputCls}
                />
              </div>
              <textarea
                required
                value={form.message}
                onChange={set("message")}
                rows={6}
                placeholder="Je bericht of event-tip — noem gerust het event, de datum en de bron *"
                data-testid="contact-message-input"
                className={`${inputCls} mt-3`}
              />
              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit-button"
                className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full bg-blaze py-3 text-sm font-bold text-black transition-transform hover:scale-[1.02] hover:bg-blaze-hover disabled:opacity-50 sm:w-auto sm:px-8"
              >
                <Send className="h-4 w-4" />
                {loading ? "Versturen..." : "Verstuur bericht"}
              </button>
            </motion.form>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
